import {
  array,
  boolean,
  custom,
  integer,
  literal,
  nullable,
  nullish,
  number,
  object,
  optional,
  recursive,
  string,
  transform,
  tuple,
  union,
  unknown
} from "valibot";
import { isValidSuiAddress, normalizeSuiAddress } from "../../utils/sui-types.js";
function safeEnum(options) {
  const unionOptions = Object.entries(options).map(([key, value]) => object({ [key]: value }));
  return transform(union(unionOptions), (value) => ({
    ...value,
    $kind: Object.keys(value)[0]
  }));
}
const SuiAddress = transform(string(), (value) => normalizeSuiAddress(value), [
  custom(isValidSuiAddress)
]);
const ObjectID = SuiAddress;
const BCSBytes = array(number([integer()]));
const JsonU64 = union(
  [string(), number([integer()])],
  [
    custom((val) => {
      try {
        BigInt(val);
        return BigInt(val) >= 0 && BigInt(val) <= 18446744073709551615n;
      } catch {
        return false;
      }
    }, "Invalid u64")
  ]
);
const ObjectRef = object({
  digest: string(),
  objectId: SuiAddress,
  version: JsonU64
});
const Argument = transform(
  union([
    object({ GasCoin: literal(true) }),
    object({ Input: number([integer()]), type: optional(literal("pure")) }),
    object({ Input: number([integer()]), type: optional(literal("object")) }),
    object({ Result: number([integer()]) }),
    object({ NestedResult: tuple([number([integer()]), number([integer()])]) })
  ]),
  (value) => ({
    ...value,
    $kind: Object.keys(value)[0]
  })
  // Defined manually to add `type?: 'pure' | 'object'` to Input
);
const GasData = object({
  budget: nullable(JsonU64),
  price: nullable(JsonU64),
  owner: nullable(SuiAddress),
  payment: nullable(array(ObjectRef))
});
const TypeTag = safeEnum({
  bool: literal(true),
  u8: literal(true),
  u64: literal(true),
  u128: literal(true),
  address: literal(true),
  signer: literal(true),
  vector: recursive(() => TypeTag),
  struct: recursive(() => StructTag),
  u16: literal(true),
  u32: literal(true),
  u256: literal(true)
});
const StructTag = object({
  address: string(),
  module: string(),
  name: string(),
  // type_params in rust, should be updated to use camelCase
  typeParams: array(TypeTag)
});
const ProgrammableMoveCall = object({
  package: ObjectID,
  module: string(),
  function: string(),
  // snake case in rust
  typeArguments: array(TypeTag),
  arguments: array(Argument)
});
const Transaction = safeEnum({
  MoveCall: ProgrammableMoveCall,
  TransferObjects: tuple([array(Argument), Argument]),
  SplitCoins: tuple([Argument, array(Argument)]),
  MergeCoins: tuple([Argument, array(Argument)]),
  Publish: tuple([array(BCSBytes), array(ObjectID)]),
  MakeMoveVec: tuple([safeEnum({ None: literal(true), Some: TypeTag }), array(Argument)]),
  Upgrade: tuple([array(BCSBytes), array(ObjectID), ObjectID, Argument])
});
const ObjectArg = safeEnum({
  ImmOrOwnedObject: ObjectRef,
  SharedObject: object({
    objectId: ObjectID,
    // snake case in rust
    initialSharedVersion: JsonU64,
    mutable: boolean()
  }),
  Receiving: ObjectRef
});
const OpenMoveTypeSignatureBody = union([
  literal("address"),
  literal("bool"),
  literal("u8"),
  literal("u16"),
  literal("u32"),
  literal("u64"),
  literal("u128"),
  literal("u256"),
  object({ vector: recursive(() => OpenMoveTypeSignatureBody) }),
  object({
    datatype: object({
      package: string(),
      module: string(),
      type: string(),
      typeParameters: array(recursive(() => OpenMoveTypeSignatureBody))
    })
  }),
  object({ typeParameter: number([integer()]) })
]);
const OpenMoveTypeSignature = object({
  ref: nullable(union([literal("&"), literal("&mut")])),
  body: OpenMoveTypeSignatureBody
});
const CallArg = safeEnum({
  Object: ObjectArg,
  Pure: BCSBytes,
  // // added for sui:unresolvedObjectIds
  UnresolvedObject: object({
    value: string(),
    typeSignatures: array(OpenMoveTypeSignature)
  }),
  // added for sui:rawValues
  RawValue: object({
    value: unknown()
  })
});
const NormalizedCallArg = safeEnum({
  Object: ObjectArg,
  Pure: BCSBytes
});
const TransactionExpiration = safeEnum({
  None: literal(true),
  Epoch: JsonU64
});
const TransactionBlockState = object({
  version: literal(2),
  features: array(string()),
  sender: nullish(SuiAddress),
  expiration: nullish(TransactionExpiration),
  gasData: GasData,
  inputs: array(CallArg),
  transactions: array(Transaction)
});
export {
  Argument,
  GasData,
  NormalizedCallArg,
  ObjectRef,
  StructTag,
  Transaction,
  TransactionBlockState,
  TransactionExpiration,
  TypeTag
};
//# sourceMappingURL=v2.js.map
